import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import dayjs from 'dayjs';
import { Fragment, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Download } from '@mui/icons-material';

import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { getSmsBillingReport, getSmsBillingReportSendings, triggerSmsBillingReportExport } from 'src/shared/services/statisticsService';
import { useExportLockHandler } from 'src/shared/functions/exportLockHandler';
import { ExportButtonEnum } from 'src/shared/models/enums/exportButton.enum';
import { SmsBillingReportAssociation, SmsBillingReportGroup, SmsBillingReportSending } from 'src/modules/statistics/models/smsBillingReportResponse';
import { useGlobals } from 'src/shared/contexts/appContext';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';

type SmsServiceFilter = {
  fromDate: string;
  toDate: string;
};

export const StatisticsSmsServicePage = () => {
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const [dataList, setDataList] = useState<SmsBillingReportGroup[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>('all');
  const [selectedAssociation, setSelectedAssociation] = useState<(SmsBillingReportAssociation & { districtLabel: string | null }) | null>(null);
  const [sendings, setSendings] = useState<SmsBillingReportSending[]>([]);
  const [isSendingsLoading, setIsSendingsLoading] = useState(false);
  const [searchDates, setSearchDates] = useState<{ fromDate: string; toDate: string } | null>(null);

  const schema: yup.Schema<SmsServiceFilter> = yup.object().shape({
    fromDate: yup.string().required(t('common:validationMessages.required')),
    toDate: yup.string().required(t('common:validationMessages.required')),
  });

  const formMethods = useForm<SmsServiceFilter>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: { fromDate: '', toDate: '' },
  });

  const { handleSubmit } = formMethods;
  const { isExportButtonLocked, lockExportBtnWhileCreating, unlockExportBtn } = useExportLockHandler();

  const onSearch = async (values: SmsServiceFilter) => {
    setIsLoading(true);
    setSelectedDistrictId('all');
    const fromDateStr = dayjs(values.fromDate).format('YYYY-MM-DD');
    const toDateStr = dayjs(values.toDate).format('YYYY-MM-DD');
    setSearchDates({ fromDate: fromDateStr, toDate: toDateStr });
    try {
      const res = await getSmsBillingReport(fromDateStr, toDateStr);
      setDataList(res.data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    lockExportBtnWhileCreating(ExportButtonEnum.SmsBillingReportExport);
    try {
      await triggerSmsBillingReportExport(searchDates?.fromDate ?? '', searchDates?.toDate ?? '', currentUser?.customerName ?? '');
      toast.success(<>{t('member:exportInitialized')}</>);
    } catch {
      unlockExportBtn(ExportButtonEnum.SmsBillingReportExport);
    }
  };

  const openAssociation = async (assoc: SmsBillingReportAssociation & { districtLabel: string | null }) => {
    setSelectedAssociation(assoc);
    setSendings([]);
    setIsSendingsLoading(true);
    try {
      const res = await getSmsBillingReportSendings(searchDates?.fromDate ?? '', searchDates?.toDate ?? '', assoc.associationId);
      setSendings(res.data);
    } finally {
      setIsSendingsLoading(false);
    }
  };

  const filteredList = dataList?.filter((g) => {
    if (selectedDistrictId === 'all') return true;
    if (selectedDistrictId === 'national') return g.districtId === null;
    return String(g.districtId) === selectedDistrictId;
  });

  const list = filteredList ?? [];
  const grandTotalCost = list.reduce((sum, g) => sum + g.associations.reduce((s, a) => s + a.cost, 0), 0);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSearch)}>
        <Grid container spacing={3}>
          <Grid item xs>
            <MainContentHeader title={t('statistics:report.smsService')} />
          </Grid>
        </Grid>
        <Grid item xs={12} mt={3}>
          <Paper sx={{ p: 2, position: 'relative' }}>
            <LoadingOverlay loading={isLoading} />
            <Grid container spacing={2} alignItems='center' mb={2}>
              <Grid item xs={12} sm={6} md={2}>
                <ControlledDatePicker name='fromDate' label={t('statistics:smsService.fromDate')} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <ControlledDatePicker name='toDate' label={t('statistics:smsService.toDate')} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size='small' disabled={!dataList}>
                  <InputLabel>{t('statistics:smsService.headers.district')}</InputLabel>
                  <Select
                    value={selectedDistrictId}
                    label={t('statistics:smsService.headers.district')}
                    onChange={(e) => setSelectedDistrictId(e.target.value)}
                  >
                    <MenuItem value='all'>{t('common:form.all')}</MenuItem>
                    {dataList?.some((g) => g.districtId === null) && <MenuItem value='national'>{currentUser?.customerName}</MenuItem>}
                    {dataList
                      ?.filter((g) => g.districtId !== null)
                      .map((g) => (
                        <MenuItem key={g.districtId} value={String(g.districtId)}>
                          {g.districtLabel}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              {dataList && dataList.length > 0 && (
                <Grid item>
                  <Button
                    variant='contained'
                    endIcon={<Download fontSize='small' />}
                    disableElevation
                    disabled={isExportButtonLocked(ExportButtonEnum.SmsBillingReportExport)}
                    onClick={handleExport}
                  >
                    {t('statistics:smsService.exportExcel')}
                  </Button>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button type='submit' variant='contained' disabled={isLoading}>
                  {t('common:button.search')}
                </Button>
              </Grid>
            </Grid>
            {dataList && !isLoading && (
              <>
                <Divider sx={{ mt: 2, mb: 2 }} />
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 600, borderBottom: (v) => `1px solid ${v.palette.divider}` } }}>
                        <TableCell width='15%'>{t('statistics:smsService.headers.federation')}</TableCell>
                        <TableCell width='15%'>{t('statistics:smsService.headers.district')}</TableCell>
                        <TableCell>{t('statistics:smsService.headers.association')}</TableCell>
                        <TableCell align='right'>{t('statistics:smsService.headers.sent')}</TableCell>
                        <TableCell align='right'>{t('statistics:smsService.headers.delivered')}</TableCell>
                        <TableCell align='right'>{t('statistics:smsService.headers.failed')}</TableCell>
                        <TableCell align='right'>{t('statistics:smsService.headers.segments')}</TableCell>
                        <TableCell align='right'>{t('statistics:smsService.headers.kostnad')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {list.map((group, groupIndex) => (
                        <Fragment key={group.districtId ?? 'national'}>
                          {group.associations.map((assoc, index) => (
                            <TableRow
                              key={`${group.districtId}-${assoc.associationId}`}
                              hover
                              sx={{ cursor: 'pointer' }}
                              onClick={() => openAssociation({ ...assoc, districtLabel: group.districtLabel })}
                            >
                              <TableCell>{index === 0 ? currentUser?.customerName : ''}</TableCell>
                              <TableCell>{index === 0 && group.districtId ? group.districtLabel : ''}</TableCell>
                              <TableCell>
                                <Box component='span' sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}>
                                  {assoc.associationLabel}
                                </Box>
                              </TableCell>
                              <TableCell align='right'>{assoc.sent}</TableCell>
                              <TableCell align='right'>{assoc.delivered}</TableCell>
                              <TableCell align='right'>{assoc.failed}</TableCell>
                              <TableCell align='right'>{assoc.segments}</TableCell>
                              <TableCell align='right'>{assoc.cost} kr</TableCell>
                            </TableRow>
                          ))}
                          {groupIndex < list.length - 1 && (
                            <TableRow sx={{ height: 24 }}>
                              <TableCell colSpan={8} sx={{ borderBottom: (v) => `1px solid ${v.palette.divider}` }} />
                            </TableRow>
                          )}
                        </Fragment>
                      ))}
                      {list.length > 0 && (
                        <TableRow sx={{ '& td': { fontWeight: 600, borderTop: (v) => `1px solid ${v.palette.divider}` } }}>
                          <TableCell colSpan={6} />
                          <TableCell align='right'>{t('statistics:smsService.total')}</TableCell>
                          <TableCell align='right'>{grandTotalCost} kr</TableCell>
                        </TableRow>
                      )}
                      {list.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align='center' sx={{ color: 'text.secondary', py: 3 }}>
                            {t('common:statusMessages.noResult')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>
        </Grid>
      </form>
      {selectedAssociation && (
        <GenericDialog
          open
          showDialogActions={false}
          maxWidth='md'
          dialogTitle={selectedAssociation.associationLabel}
          dialogSubTitle={selectedAssociation.districtLabel ?? currentUser?.customerName}
          onClose={() => setSelectedAssociation(null)}
          onSave={() => setSelectedAssociation(null)}
          dialogContent={
            <Box sx={{ p: 1 }}>
              <Grid container spacing={2} mb={2}>
                {[
                  { label: t('statistics:smsService.headers.sent'), value: selectedAssociation.sent, color: 'primary.main' },
                  { label: t('statistics:smsService.headers.delivered'), value: selectedAssociation.delivered, color: 'success.main' },
                  {
                    label: t('statistics:smsService.headers.failed'),
                    value: selectedAssociation.failed,
                    color: selectedAssociation.failed > 0 ? 'warning.main' : null,
                  },
                  { label: t('statistics:smsService.headers.segments'), value: selectedAssociation.segments, color: null },
                  { label: t('statistics:smsService.headers.kostnad'), value: `${selectedAssociation.cost} kr`, color: null },
                ].map(({ label, value, color }) => (
                  <Grid item xs={6} sm={4} md key={label}>
                    <Paper
                      elevation={0}
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderTop: '1px solid',
                        borderRight: '1px solid',
                        borderBottom: '1px solid',
                        borderTopColor: 'divider',
                        borderRightColor: 'divider',
                        borderBottomColor: 'divider',
                        borderLeft: color ? '4px solid' : '1px solid',
                        borderLeftColor: color ?? 'divider',
                      }}
                    >
                      <Typography variant='h5' fontWeight={600} color='text.primary'>
                        {value}
                      </Typography>
                      <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ mb: 2 }} />
              {isSendingsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 600, borderBottom: (v) => `2px solid ${v.palette.divider}` } }}>
                        <TableCell>{t('statistics:smsService.headers.sentDate')}</TableCell>
                        <TableCell>{t('statistics:smsService.headers.sendingName')}</TableCell>
                        <TableCell>{t('statistics:smsService.headers.senderLabel')}</TableCell>
                        <TableCell align='right'>{t('statistics:smsService.headers.sent')}</TableCell>
                        <TableCell align='right'>{t('statistics:smsService.headers.delivered')}</TableCell>
                        <TableCell align='right'>{t('statistics:smsService.headers.failed')}</TableCell>
                        <TableCell align='right'>{t('statistics:smsService.headers.segments')}</TableCell>
                        <TableCell align='right'>{t('statistics:smsService.headers.kostnad')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sendings.map((s) => (
                        <TableRow key={s.sendingId}>
                          <TableCell>{s.sendDate ? new Date(s.sendDate).toLocaleDateString('sv-SE') : ''}</TableCell>
                          <TableCell>{s.sendingName}</TableCell>
                          <TableCell>{s.sender}</TableCell>
                          <TableCell align='right'>{s.sent}</TableCell>
                          <TableCell align='right'>{s.delivered}</TableCell>
                          <TableCell align='right'>{s.failed}</TableCell>
                          <TableCell align='right'>{s.segments}</TableCell>
                          <TableCell align='right'>{s.cost} kr</TableCell>
                        </TableRow>
                      ))}
                      {sendings.length > 0 && (
                        <TableRow sx={{ '& td': { fontWeight: 600, borderTop: (v) => `2px solid ${v.palette.divider}` } }}>
                          <TableCell colSpan={3}>{t('statistics:smsService.totalSendings', { count: sendings.length })}</TableCell>
                          <TableCell align='right'>{sendings.reduce((sum, s) => sum + s.sent, 0)}</TableCell>
                          <TableCell align='right'>{sendings.reduce((sum, s) => sum + s.delivered, 0)}</TableCell>
                          <TableCell align='right'>{sendings.reduce((sum, s) => sum + s.failed, 0)}</TableCell>
                          <TableCell align='right'>{sendings.reduce((sum, s) => sum + s.segments, 0)}</TableCell>
                          <TableCell align='right'>{sendings.reduce((sum, s) => sum + s.cost, 0)} kr</TableCell>
                        </TableRow>
                      )}
                      {sendings.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align='center' sx={{ color: 'text.secondary', py: 3 }}>
                            {t('common:statusMessages.noResult')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          }
        />
      )}
    </FormProvider>
  );
};
